/**
 * Thin wrappers over Nova OS's /api/documents endpoints, scoped to the
 * Teaching Plan and Curriculum subdirs. Nova OS rewrites every path
 * under users/<user_id>/ server-side for non-admin roles, so these
 * call sites don't have to worry about user scoping.
 *
 * Routes used:
 *   POST   /api/documents/upload/<path>/    (multipart "file")
 *   GET    /api/documents/tree?path=<path>  (folders + files list)
 *   GET    /api/documents/download/<path>   (raw bytes / text — was
 *                                            initially wired to /get/
 *                                            which doesn't exist;
 *                                            /download/ is canonical)
 *   DELETE /api/documents/file/<path>       (删除并级联清检索块;
 *       /rm/ 端点不级联 —— 2026-08-12 bosong 孤儿块事故,勿改回)
 *
 * The upload endpoint rewrites the form-data filename into the trailing
 * URL path. Empty path lands the file in the user's home dir; a path
 * segment lands it under that subdir, which is how we keep plans + the
 * agenda namespaced under /documents/<feature>/.
 */
import { authHeaders } from "../../api/school";

export const PLANS_PATH = "teaching-plans";
export const CURRICULA_PATH = "curricula";

/** 大纲按学科分目录:curricula/<slug>/。
 *
 *  学科维度是 2026-08-28 补的。在那之前所有大纲平铺在 curricula/ 下,
 *  多学科老师传了数学和物理两份,生成计划时取的是列表里的最后一个 ——
 *  和表单上选中的学科完全无关(Jarod 反馈)。旧文件不迁移(无从反推
 *  它属于哪一科),按「未分学科」回落继续可用。 */
export function curriculaPathFor(slug?: string | null): string {
  return slug ? `${CURRICULA_PATH}/${slug}` : CURRICULA_PATH;
}

export interface DocTreeFile {
  name: string;
  doc_id?: string | null;
  status?: string | null;
  /** RFC3339 mtime from the server; "" / absent on older nova-os builds. */
  created_at?: string | null;
}

/** Newest-first by created_at; files without a timestamp sort last (by name). */
export function sortFilesByTimeDesc<T extends DocTreeFile>(files: T[]): T[] {
  return [...files].sort((a, b) => {
    const ta = a.created_at || "";
    const tb = b.created_at || "";
    if (ta && tb) return tb.localeCompare(ta);
    if (ta) return -1;
    if (tb) return 1;
    return a.name.localeCompare(b.name);
  });
}

/** Compact local-time label for file rows, e.g. "7/27 14:05". */
export function formatFileTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface DocTree {
  path: string;
  folders?: string[];
  files?: DocTreeFile[];
}

export async function listDocs(path: string): Promise<DocTree> {
  const r = await fetch(
    `/api/documents/tree?path=${encodeURIComponent(path)}`,
    { headers: authHeaders() },
  );
  if (!r.ok) throw new Error(`tree ${r.status}`);
  return (await r.json()) as DocTree;
}

/** Upload a file (Blob or File) to the given subdir. The trailing
 *  slash on the URL is significant — Nova OS routes /upload/<path>/
 *  to "store under this subdir"; without it the behavior is "use the
 *  literal filename as the leaf". We always include it so callers
 *  don't have to think about that. */
export async function uploadDoc(
  subdir: string,
  filename: string,
  body: Blob,
  collection?: string,
): Promise<void> {
  const fd = new FormData();
  fd.append("file", new File([body], filename, { type: body.type || "text/markdown" }));
  // 集合只由显式参数决定,路径不参与(nova-os documents.go 的语义)。
  // 不带 collection 时管理员上传一律落 default —— 学科页必须带,否则
  // subject_* 集合永远是空的(Jarod 2026-08-12 全量重传踩坑)。
  if (collection) fd.append("collection", collection);
  // Append a slash if the caller didn't, so the trailing-path semantics
  // match what Nova OS expects for subdir routing.
  const path = subdir.endsWith("/") ? subdir : `${subdir}/`;
  const r = await fetch(`/api/documents/upload/${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `upload ${r.status}`);
  }
}

/** Read a doc's bytes as text. Used to load plan markdown for the
 *  edit screen, and to extract a text-extractable agenda when
 *  generating new plans. */
export async function readDocText(path: string): Promise<string> {
  const r = await fetch(`/api/documents/download/${encodeURIComponent(path)}`, {
    headers: authHeaders(),
  });
  if (!r.ok) throw new Error(`download ${r.status}`);
  return await r.text();
}

/** 读成可喂给 AI 的纯文本:html/mhtml 剥掉标签与样式,只留正文。
 *  readDocText 保持原样(FolderPanel 的"编辑原文"依赖原始字节)。
 *  Jarod 2026-08-27:HTML 教学大纲传上去后 AI 说"无法识别"。 */
export async function readDocTextForPrompt(path: string, filename: string): Promise<string> {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  let html: string | null = null;
  if (ext === "mhtml" || ext === "mht") {
    const { parseMhtml } = await import("../../lib/mhtml");
    const r = await fetch(`/api/documents/download/${encodeURIComponent(path)}`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error(`download ${r.status}`);
    html = parseMhtml(await r.arrayBuffer()).html;
  } else if (ext === "html" || ext === "htm") {
    html = await readDocText(path);
  }
  if (html === null) return await readDocText(path);
  // DOMParser 不执行脚本,取正文文字即可(样式/脚本节点先摘掉)。
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, noscript").forEach((n) => n.remove());
  return (doc.body?.innerText ?? doc.body?.textContent ?? "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export async function deleteDoc(path: string): Promise<void> {
  const r = await fetch(`/api/documents/file/${encodeURIComponent(path)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `rm ${r.status}`);
  }
}

/** 学科知识库的文件面走 school-backend 代理,而不是直连 nova-os。
 *
 *  原因:园长(nova 角色 manager)在 nova-os 眼里是普通用户 —— 文档路径
 *  被重写到 users/<uid>/、写权限只认自己的个人库。于是学科管理页对她
 *  四样全不通:列表 0 条、上传 403、删除 404、下载 404。后端代理先校验
 *  是不是园长/管理员,再以服务身份执行(routers/subject_files.py)。
 *
 *  管理员走这条路结果一样,所以前端不分叉。 */
export const subjectFilesApi = {
  async list(slug: string): Promise<DocTree> {
    const r = await fetch(`/api/school/subjects/${encodeURIComponent(slug)}/files`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error(`subject files ${r.status}`);
    return (await r.json()) as DocTree;
  },
  async upload(slug: string, filename: string, body: Blob): Promise<void> {
    const fd = new FormData();
    fd.append("file", new File([body], filename, { type: body.type || "application/octet-stream" }));
    const r = await fetch(`/api/school/subjects/${encodeURIComponent(slug)}/files`, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    if (!r.ok) {
      const b = (await r.json().catch(() => ({}))) as { detail?: string };
      throw new Error(b.detail ?? `upload ${r.status}`);
    }
  },
  async remove(slug: string, filename: string): Promise<void> {
    const r = await fetch(
      `/api/school/subjects/${encodeURIComponent(slug)}/files/${encodeURIComponent(filename)}`,
      { method: "DELETE", headers: authHeaders() },
    );
    if (!r.ok && r.status !== 404) throw new Error(`delete ${r.status}`);
  },
  downloadUrl(slug: string, filename: string): string {
    return `/api/school/subjects/${encodeURIComponent(slug)}/files/${encodeURIComponent(filename)}/download`;
  },
};

/** 集团知识库(院所信息)的文件面 —— 同样走 school-backend 代理。
 *
 *  读全员、写园长。两边都必须代理:上传时 `collection=default` 对非 admin
 *  会 403(园长的 nova 角色是 manager);读取时非管理员的路径会被重写到
 *  `users/<id>/`,老师直连只会得到空列表。授权判断在后端
 *  routers/library_files.py。 */
export const libraryFilesApi = {
  async list(): Promise<DocTree> {
    const r = await fetch("/api/school/library/files", { headers: authHeaders() });
    if (!r.ok) throw new Error(`library files ${r.status}`);
    return (await r.json()) as DocTree;
  },
  async upload(filename: string, body: Blob): Promise<void> {
    const fd = new FormData();
    fd.append("file", new File([body], filename, { type: body.type || "application/octet-stream" }));
    const r = await fetch("/api/school/library/files", {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    if (!r.ok) {
      const b = (await r.json().catch(() => ({}))) as { detail?: string };
      throw new Error(b.detail ?? `upload ${r.status}`);
    }
  },
  async remove(filename: string): Promise<void> {
    const r = await fetch(`/api/school/library/files/${encodeURIComponent(filename)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!r.ok && r.status !== 404) throw new Error(`delete ${r.status}`);
  },
  /** 取文件字节。**不能用裸 <a href> 下载** —— 那不带 Authorization 头,
   *  代理端会 401。调用方拿到 Blob 后自己造 object URL。 */
  async download(filename: string): Promise<Blob> {
    const r = await fetch(
      `/api/school/library/files/${encodeURIComponent(filename)}/download`,
      { headers: authHeaders() },
    );
    if (!r.ok) throw new Error(`download ${r.status}`);
    return await r.blob();
  },
};
